import React, {
  useState,
  useEffect,
  useRef,
  memo,
  useCallback,
  useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import { useDropzone } from 'react-dropzone'

import { ArrowDownwardRounded as DownIcon } from '@material-ui/icons'

import {
  setConversations,
  setFileDrop,
} from '../../../redux/conversations/actions'

import BalloonSystem from '../../atoms/balloons/balloonSystem'
import BalloonConsumer from '../../atoms/balloons/balloonConsumer'
import BalloonTime from '../../atoms/balloons/balloonTime'
import BalloonAgent from '../../atoms/balloons/balloonAgent'
import Button from '../../atoms/inputs/button'
import Gallery from '../../organisms/galleries/chatPictures'
import HistoryConversation from '../../organisms/panels/historyConversation'

import useStyles from './conversationStyle'
import BallonSummary from '../../atoms/balloons/ballonSummary'
import { useAutomaticSummarization } from '../../../hooks/useAutomaticSummarization'

const Conversation = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles()

  const activeConversation = useSelector((state) => state.activeConversation)
  const conversations = useSelector(
    (state) => state.conversations.conversations
  )
  const messages = useSelector((state) => state.conversations.messages)

  const [galleryOpen, setGalleryOpen] = useState(false)
  const [isDrop, setIsDrop] = useState(false)
  const [scrollConversations, setScrollConversations] = useState({})
  const [maskConversations, setMaskConversations] = useState({})

  let counter = 0
  const scrollRef = useRef()
  const messagesEndRef = useRef(null)

  const maskConversationActive = useMemo(() => {
    return maskConversations?.[activeConversation]?.mask ?? false
  }, [maskConversations, activeConversation])

  const isSkillSummaryActived = useMemo(() => {
    return conversations?.[activeConversation]?.isAiSummaryEnabled ?? false
  }, [conversations, activeConversation])

  const messagesUnread = useMemo(() => {
    return (
      messages[activeConversation]
        ?.filter((msg) => msg.unread)
        ?.reduce((acc, _) => acc + 1, 0) ?? 0
    )
  }, [messages, activeConversation])

  const scrollMaxTop = useMemo(() => {
    const offsetHeight = scrollRef?.current?.offsetHeight ?? 0
    const scrollHeight = scrollRef?.current?.scrollHeight ?? 0
    const maxTop = offsetHeight - scrollHeight
    return Math.abs(maxTop)
  }, [scrollRef?.current?.offsetHeight, scrollRef?.current?.scrollHeight])

  const scrollPositionActiveConversation = useMemo(() => {
    return scrollConversations?.[activeConversation]?.scroll ?? scrollMaxTop
  }, [scrollConversations, activeConversation, scrollMaxTop])

  const conversationConsumerId = useMemo(() => {
    return conversations[activeConversation]?.consumer?.id
  }, [conversations, activeConversation])

  const conversationChannel = useMemo(() => {
    return conversations[activeConversation]?.channel
  }, [conversations, activeConversation])

  const scrollToBottomControll = useMemo(() => {
    let messagesCv = messages[activeConversation] ?? []
    messagesCv = messagesCv[messagesCv?.length - 1] ?? null
    if (
      messagesCv &&
      messagesCv?.by === 'agent' &&
      messagesCv?.unread === true
    ) {
      return {
        toBottom: true,
        by: 'agent',
        unread: messagesCv?.unread ?? false,
      }
    }
    return {
      toBottom: !maskConversationActive && messagesUnread > 0,
      by: messagesCv?.by ?? null,
      unread: messagesCv?.unread ?? false,
    }
  }, [maskConversationActive, messagesUnread, messages, activeConversation])

  const scrollToBottom = useCallback(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messagesEndRef?.current?.scrollIntoView])

  const scrollToBottomInstant = useCallback(() => {
    const target = document.getElementById('conversation')
    if (target) {
      const maxTop = Math.abs(target?.offsetHeight - target?.scrollHeight)
      target?.scrollTo({
        top: maxTop,
        behavior: 'instant',
      })
    }
  }, [])

  const updateUnreadMsg = useCallback(() => {
    const msgs = messages?.[activeConversation]?.map((msg) =>
      msg?.unread ? { ...msg, unread: false } : msg
    )

    dispatch(
      setConversations({
        ...conversations[activeConversation],
        message: msgs,
      })
    )
  }, [messages, activeConversation])

  const onScroll = useCallback(
    ({ target }) => {
      const scrollTop = Math.ceil(target.scrollTop)
      const delta = target.offsetHeight + scrollTop - target.scrollHeight
      const maxTop = Math.abs(target.offsetHeight - target.scrollHeight)
      const limit = Math.ceil(maxTop * 0.97)
      const minimoDistance = maxTop - scrollTop <= 120

      setScrollConversations((old) => {
        return {
          ...old,
          [activeConversation]: {
            conversationId: activeConversation,
            scroll: scrollTop,
            delta: delta,
          },
        }
      })

      if (delta >= 0 || minimoDistance) {
        if (maskConversationActive) {
          setMaskConversations((old) => ({
            ...old,
            [activeConversation]: {
              mask: false,
            },
          }))
        }

        updateUnreadMsg()
      } else if (scrollTop > limit && !maskConversationActive) {
        setMaskConversations((old) => ({
          ...old,
          [activeConversation]: {
            mask: false,
          },
        }))
      } else if (!maskConversationActive && maxTop >= 220) {
        setMaskConversations((old) => ({
          ...old,
          [activeConversation]: {
            mask: true,
          },
        }))
      }
    },
    [activeConversation, updateUnreadMsg, maskConversationActive]
  )

  const onDrop = useCallback((file) => {
    dispatch(setFileDrop(file))
    setIsDrop(false)
  }, [])

  const { getRootProps } = useDropzone({
    multiple: true,
    onDrop,
  })

  useEffect(() => {
    let chatdroparea = document?.getElementById('dd')
    chatdroparea?.addEventListener('dragenter', (e) => {
      e.stopPropagation()
      e.preventDefault()
      counter++
      setIsDrop(true)
    })
    chatdroparea?.addEventListener('dragleave', (e) => {
      counter--
      if (counter === 0) {
        setIsDrop(false)
      }
    })
    chatdroparea?.addEventListener('drop', (e) => {
      setTimeout(() => {
        counter = 0
        setIsDrop(false)
      }, 300)
    })
    chatdroparea?.addEventListener('dragover', (e) => {
      if (counter === 0) {
        setIsDrop(false)
      }
    })
    return () => {
      chatdroparea?.removeEventListener('dragenter', (e) => {
        setIsDrop(true)
      })
      chatdroparea?.removeEventListener('dragleave', (e) => {
        setIsDrop(false)
      })
      chatdroparea?.removeEventListener('drop', (e) => {
        setIsDrop(false)
      })
      chatdroparea?.removeEventListener('dragover', (e) => {
        setIsDrop(false)
      })
    }
  }, [])

  useEffect(() => {
    if (scrollPositionActiveConversation >= 0) {
      scrollRef?.current?.scrollTo({
        top: scrollPositionActiveConversation,
        behavior: 'instant',
      })
    }
  }, [activeConversation, scrollRef?.current?.scrollTo])

  useEffect(() => {
    if (!scrollConversations[activeConversation]) {
      scrollToBottomInstant()
    }
  }, [activeConversation, scrollConversations, scrollToBottomInstant])

  useEffect(() => {
    if (scrollToBottomControll?.toBottom) {
      const target = document.getElementById('conversation')
      const maxTop = Math.abs(target?.offsetHeight - target?.scrollHeight)
      const scrollTop = target?.scrollTop

      if (
        messagesUnread > 1 &&
        scrollToBottomControll?.by !== 'agent' &&
        maxTop > scrollTop
      ) {
        target?.scrollTo({
          top: maxTop,
          behavior: 'instant',
        })
      } else {
        scrollToBottom()
      }

      updateUnreadMsg()
    }
  }, [
    scrollToBottomControll,
    scrollToBottom,
    updateUnreadMsg,
    activeConversation,
    messagesUnread,
  ])

  return (
    <>
      <div
        id="conversation"
        className={classes.content}
        onScroll={onScroll}
        ref={scrollRef}
      >
        {messages[activeConversation]?.length > 0 ? (
          <RenderMessages
            messages={messages[activeConversation]}
            unreadMsgs={messagesUnread}
            conversationId={activeConversation}
            isSkillSummaryActived={isSkillSummaryActived}
          />
        ) : (
          <HistoryConversation
            consumerId={conversationConsumerId}
            channel={conversationChannel}
            chatId={activeConversation}
          />
        )}
        <RenderMask
          mask={maskConversationActive}
          unreadMsgs={messagesUnread}
          bottomHeight={props?.bottomHeight}
          scrollToBottom={scrollToBottom}
          updateUnreadMsg={updateUnreadMsg}
          classes={classes}
        />

        <div ref={messagesEndRef} />
      </div>

      {isDrop && (
        <div className={classes.dropzone} {...getRootProps()}>
          <p className={classes.dropzoneText}>{t('commons.dragdrop')}</p>
        </div>
      )}

      {galleryOpen && (
        <Gallery open={galleryOpen} openCallback={() => setGalleryOpen()} />
      )}
    </>
  )
}

const RenderMask = ({
  mask,
  unreadMsgs,
  bottomHeight,
  scrollToBottom,
  updateUnreadMsg,
  classes,
}) => {
  const { t } = useTranslation()

  const textMask = useCallback(() => {
    if (unreadMsgs > 0) {
      if (unreadMsgs === 1) {
        return `${unreadMsgs} ${t('commons.newMessage')}`
      } else {
        return `${unreadMsgs} ${t('commons.newMessages')}`
      }
    } else {
      return <></>
    }
  }, [unreadMsgs])

  const onClickButton = useCallback(() => {
    scrollToBottom()
    updateUnreadMsg()
  }, [scrollToBottom, updateUnreadMsg])

  if (mask) {
    return (
      <>
        <div className={classes.mask} style={{ bottom: bottomHeight }}>
          <Button
            size="large"
            variant="contained"
            color="primary"
            border="rounded"
            startIcon={
              <DownIcon style={{ marginLeft: unreadMsgs > 0 ? 0 : 10 }} />
            }
            onClick={onClickButton}
          >
            {textMask()}
          </Button>
        </div>
      </>
    )
  }

  return <></>
}

const RenderMessages = ({
  messages,
  unreadMsgs,
  conversationId,
  isSkillSummaryActived,
}) => {
  const { t } = useTranslation()
  const { refresh } = useAutomaticSummarization()

  let dividerText = null
  let firstUread = messages?.find((msg) => msg.unread)

  useEffect(() => {
    if (conversationId && isSkillSummaryActived) 
      refresh({ isAutomaticSummarizationEnabled: isSkillSummaryActived })
  }, [conversationId, isSkillSummaryActived])

  const renderBallonBySenderType = useCallback(
    (el) => {
      if (el.by === 'system' || el.by === 'bot') {
        return <BalloonSystem data={el} showBookmark={true} resize={true} />
      }
      if (el.by === 'consumer') {
        return <BalloonConsumer data={el} showBookmark={true} resize={true} />
      }
      if (el.by === 'agent' || el.by === 'manager') {
        return <BalloonAgent data={el} showBookmark={true} resize={true} />
      }

      return (
        <BallonSummary
          data={el}
          conversationId={conversationId}
          isSkillSummaryActived={isSkillSummaryActived}
        />
      )
    },
    [conversationId]
  )

  return messages?.map((el) => {
    if (!dividerText || dividerText !== moment(el.sentTime).format('DD/MM')) {
      dividerText = moment(el.sentTime).format('DD/MM')
      return (
        <>
          {unreadMsgs > 0 && firstUread?.currentId === el?.currentId ? (
            <BalloonTime time={t('buttons.newMessages')} />
          ) : (
            <BalloonTime time={dividerText} />
          )}
          {renderBallonBySenderType(el)}
        </>
      )
    } else {
      return (
        <>
          {unreadMsgs > 0 && firstUread?.currentId === el?.currentId && (
            <BalloonTime time={t('buttons.newMessages')} />
          )}
          {renderBallonBySenderType(el)}
        </>
      )
    }
  })
}

export default memo(Conversation)
